@if(visible) {
<app-stepper [config]="stepperConfig" [visible]="visible" (visibleChange)="visible = $event; visibleChange.emit($event)"
    (close)="cancel()" (finish)="callAIAssistant()" (nextStepClick)="onNextStep()" (stepChange)="onStepChange($event)"
    [finishButtonLoading]="generationInProgress" [disableNextButton]="isNextDisabled()">

    <!-- Step 1: Customisation -->
    <div *stepperStep="customisationStepConfig" class="flex flex-col gap-4 h-full overflow-y-auto">

        @if(aiAssistant.loading) {
        <div class="flex flex-col font-semibold items-center justify-center p-12">
            <img class="h-[12rem] w-[10rem] animate-pulse" [src]="'images/insights-image.png' | assetUrl" alt="image">
            <span class="theme-text animate-pulse">{{aiAssistant.stage | textCase}}</span>
        </div>
        } @else {

        <p-accordion [multiple]="true" [(value)]="openAccordionPanels" class="ai-assistant-accordion">

            <p-accordion-panel value="customisation">
                <p-accordion-header>{{"Customization" | textCase}}</p-accordion-header>
                <p-accordion-content>
                    <div class="flex flex-col gap-4">

                        <div class="flex flex-col gap-2 w-full">
                            <label class="text-sm font-semibold text-gray-500">{{"Document type" |
                                textCase}} <span class="text-red-500">*</span></label>
                            <app-select [(value)]="selectedDocTypeId" [options]="docTypeOptions"
                                [config]="docTypeConfig" [itemTemplate]="docTypeItem"
                                [selectedItemTemplate]="selectedDocTypeItem" (onChange)="handleDocTypeChange($event)">
                            </app-select>

                            <ng-template #docTypeItem let-option>
                                <div class="flex flex-col max-w-full">
                                    <span class="font-bold">{{option.data?.name || option.label}}</span>
                                    <span class="text-gray-500 doc-type-dropdown">{{option.data?.description}}</span>
                                </div>
                            </ng-template>

                            <ng-template #selectedDocTypeItem let-option>
                                <div class="flex flex-col max-w-full">
                                    <span class="font-bold">{{option.data?.name || option.label}}</span>
                                </div>
                            </ng-template>
                        </div>

                        <div class="flex flex-col gap-2 w-full">
                            <label class="text-sm font-semibold text-gray-500">{{"Enter prompt for the document" |
                                textCase}} <span class="text-red-500">*</span></label>
                            <textarea pInputTextarea rows="3" class="rounded-base ai-text-area" id="aiTextArea"
                                [disabled]="loading" placeholder="Ask AI Assistant to generate document"
                                [(ngModel)]="content" (ngModelChange)="checkWordLimit()"></textarea>
                            @if(wordCount > wordLimit) {
                            <p class="text-xs text-right text-danger">{{"Maximum word limit is 500" | textCase}}</p>
                            } @else {
                            <p class="text-xs text-right -mt-1">{{ wordCount }} / {{wordLimit}} {{"words" | textCase}}
                            </p>
                            }
                            <div class="flex flex-col gap-1">
                                <div
                                    class="flex flex-row w-full my-2 gap-2 overflow-x-auto whitespace-nowrap no-scrollbar">
                                    @for(question of promptQuestions; track $index) {
                                    <div class="cursor-pointer theme-border rounded-base text-xs text-center font-semibold px-2 py-1"
                                        [class.pointer-events-none]="loading" (click)="sendPrompt(question)">
                                        {{question}}
                                    </div>
                                    }
                                </div>
                                <div class="flex items-center gap-1 text-sm text-gray-600">
                                    <img src="icons/template-library/ai_icon.svg" alt="Inndoc AI" class="w-5 h-5">
                                    <span class="italic theme-text text-sm">Powered by InnDoc AI</span>
                                </div>
                            </div>
                        </div>

                        <div class="flex flex-col gap-2 w-full">
                            <label class="text-sm font-semibold text-gray-500">{{"Project" | textCase}}</label>
                            <app-select #projectSelect [(value)]="selectedProjectId" [options]="projectOptions"
                                [config]="{ placeholder: 'Select a Project', searchable: true, clearable: true, loading: projectsLoading, appendTo: 'body', backendSearch: true, infiniteScroll: true }"
                                (onSearch)="onProjectSearch($event)" (onLoadMore)="onProjectLoadMore()">
                            </app-select>
                        </div>

                        <div class="flex flex-col gap-2 w-full">
                            <label class="text-sm font-semibold text-gray-500">{{"Due Date" | textCase}}</label>
                            <app-datepicker [(value)]="dueDate" [config]="dueDateConfig" />
                        </div>

                        <div class="flex flex-col gap-2 w-full">
                            <label class="text-sm font-semibold text-gray-500">{{"Tone" |
                                textCase}}</label>
                            <app-menu #toneMenu [items]="toneMenuItems" [selectedValues]="selectedToneMenuValues"
                                [visible]="toneMenuVisible" (visibleChange)="toneMenuVisible = $event"
                                (itemSelected)="onToneSelected($event)">
                                <app-button variant="outlined" severity="contrast" iconPos="left"
                                    (clicked)="toneMenu.toggle($event)" toolTip="Tone" tooltipPosition="top"
                                    [enableThrottle]="true" class="w-full text-sm justify-between h-[2.5rem]">
                                    <div class="flex gap-1 items-center">
                                        @if(selectedToneMenuValues[0] && selectedToneMenuValues[0].icon) {
                                        <app-icon [type]="selectedToneMenuValues[0].icon || ''"
                                            [source]="selectedToneMenuValues[0].iconSource || IconSourceEnum.PRIMENG"
                                            [size]="'sm'" />
                                        }
                                        <span class="truncate">{{ selectedToneLabel }}</span>
                                    </div>
                                    <app-icon type="pi pi-chevron-down" source="primeng" size="sm" />
                                </app-button>
                            </app-menu>
                        </div>




                    </div>
                </p-accordion-content>
            </p-accordion-panel>

            <p-accordion-panel value="milestones" [disabled]="!selectedDocType">
                <p-accordion-header>
                    <span [class.text-gray-400]="!selectedDocType">{{"Contract Milestones" | textCase}}</span>
                </p-accordion-header>
                <p-accordion-content>
                    @if(milestonesLoading) {
                    <div class="flex justify-center items-center py-6">
                        <i class="pi pi-spin pi-spinner text-xl text-arena-orange"></i>
                    </div>
                    } @else {
                    <app-milestones [milestones]="milestones" [showHeader]="false" [showActions]="false"
                        (milestonesChange)="onMilestonesChange($event)">
                    </app-milestones>
                    }
                </p-accordion-content>
            </p-accordion-panel>

        </p-accordion>

        }
    </div>

    <!-- Step 2: Template -->
    <div *stepperStep="templateStepConfig" class="flex-1 flex flex-col min-h-0 overflow-hidden">
        <ng-container *ngTemplateOutlet="libraryTemplate"></ng-container>
    </div>

    <!-- Step 3: Reference Documents -->
    <div *stepperStep="referenceDocsStepConfig" class="h-full overflow-hidden">
        <ng-container *ngTemplateOutlet="referenceDocumentsTemplate"></ng-container>
    </div>

    <!-- Step 4: Team -->
    <div *stepperStep="teamStepConfig">
        <app-share-to-users [shareDetails]="shareDetails" [isDocumentForm]="true"
            (removeUserFromShare)="onRemoveUserFromShare($event)" (updateSharedUser)="onShareUpdateUser($event)"
            (changeAnyoneAccess)="shareDetails.anyone = $event">
        </app-share-to-users>
    </div>

</app-stepper>
}

<!-- Library template -->
<ng-template #libraryTemplate>
    <div class="flex flex-col h-full rounded-xl bg-white overflow-hidden">

        <div class="flex flex-col flex-1 min-h-0 gap-3">

            <div class="flex items-center justify-between gap-3 flex-shrink-0">
                <div class="w-full sm:w-64 md:w-80">
                    <app-search-bar [(ngModel)]="librarySearchValue" (searchChange)="onLibrarySearchChange()"
                        placeholder="Search template name / ID" width="100%">
                    </app-search-bar>
                </div>
                <app-button variant="outlined" severity="contrast" icon="pi pi-plus" label="New template"
                    (clicked)="onNewTemplate()">
                </app-button>
            </div>

            @if(libraryLoading) {
            <div class="flex flex-1 items-center justify-center">
                <i class="pi pi-spin pi-spinner text-2xl text-arena-orange"></i>
            </div>
            } @else {
            <div class="flex-1 overflow-y-auto min-h-0">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    @for (item of libraryTemplates; track item._id) {
                    <div (click)="selectLibraryItem(item._id)"
                        class="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-white shadow-sm cursor-pointer hover:shadow-md transition min-h-[5rem]"
                        [ngClass]="{'border-arena-orange-500 bg-arena-orange-50': selectedLibraryItemId === item._id}">

                        <div class="flex-shrink-0" (click)="onTemplateRadioClick($event, item._id)">
                            <p-radioButton name="libraryItem" [value]="item._id" [(ngModel)]="selectedLibraryItemId"
                                [inputId]="'lib-' + item._id">
                            </p-radioButton>
                        </div>

                        <div class="flex flex-col flex-1 min-w-0">
                            <span class="text-sm text-gray-500">
                                ID - {{ item.templateId || item._id.slice(-4) }}
                            </span>
                            <span class="text-sm font-semibold text-gray-900 line-clamp-2" [pTooltip]="item.name">
                                {{ item.name || 'Untitled' }}
                            </span>
                        </div>

                        <div class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100"
                            (click)="$event.stopPropagation(); onViewLibraryItem(item)" pTooltip="View">
                            <app-icon type="pi pi-eye" source="primeng" size="lg"></app-icon>
                        </div>
                    </div>
                    }
                </div>

                @if(libraryTemplates.length === 0) {
                <div class="flex flex-1 flex-col items-center justify-center gap-4 py-8">
                    <img class="w-1/2 h-1/2 object-contain" src="images/templates_not_found.png">
                    <p class="text-sm text-gray-500">No templates found</p>
                </div>
                }
            </div>
            }

        </div>

        @if(libraryTotalRecords > 0 && !libraryLoading) {
        <div class="flex-shrink-0">
            <p-paginator class="w-full" [first]="libraryFirst" [rows]="libraryRows" [totalRecords]="libraryTotalRecords"
                [rowsPerPageOptions]="libraryRowsPerPageOptions" (onPageChange)="onLibraryPageChange($event)"
                [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first} to {last} of {totalRecords}">
            </p-paginator>
        </div>
        }
    </div>
</ng-template>

<!-- Reference Documents template -->
<ng-template #referenceDocumentsTemplate>
    <app-file-selection class="h-full" [mode]="FileSelectionMode.INLINE" [config]="fileSelectionConfig"
        [fetch]="fileSelectionFetch" [dropdownOptions]="fileSelectionDropdownOptions"
        [afterDropdownTemplate]="integrationsConnectorsTemplate" [afterDropdownTemplateTabId]="'INTEGRATIONS'"
        [emptyStateTemplate]="fileSelectionEmptyState" (onSubmit)="onFileSelectionSubmit($event)"
        (onCancel)="onFileSelectionCancel()">
    </app-file-selection>
</ng-template>

<!-- Integrations connectors template -->
<ng-template #integrationsConnectorsTemplate>
    <app-menu #connectorsMenu [items]="integrationsConnectorsMenuItems" [class]="'!max-h-[40vh] overflow-y-auto'"
        (itemSelected)="onIntegrationsConnectorSelect($event, connectorsMenu)" [pTooltip]="'Connectors'" tooltipPosition="top">
        <app-button [fullWidth]="true" [icon]="'blocks'" [iconSource]="'lucide'" [iconSize]="'default'"
            variant="outlined" severity="contrast" label="Connectors" (clicked)="connectorsMenu.toggle($event)" />
    </app-menu>
</ng-template>

<!-- File selection empty state template -->
<ng-template #fileSelectionEmptyState let-tab="tab" let-emptyMessage="emptyMessage"
    let-selectedDropdown="selectedDropdown">
    @if(tab?.id === 'INTEGRATIONS') {
    @if(getIntegrationConnectorConnected(selectedDropdown)) {
    <div class="flex flex-col items-center justify-center h-full text-gray-500">
        <img [src]="'images/bob_agent/404-bob.svg' | assetUrl" class="w-[80%]" />
        <p>{{ emptyMessage }}</p>
    </div>
    } @else {
    <div class="flex flex-col items-center justify-center h-full text-gray-500 px-4">
        <img [src]="'images/integrations-no-connection.svg' | assetUrl" class="w-[80%] max-w-xs" />
        <p class="text-sm mt-1 text-center">Connect your apps to start importing files</p>
    </div>
    }
    } @else {
    <div class="flex flex-col items-center justify-center h-full text-gray-500">
        <img [src]="'images/bob_agent/404-bob.svg' | assetUrl" class="w-[80%]" />
        <p>{{ emptyMessage }}</p>
    </div>
    }
</ng-template>


<!-- Template preview dialog -->
<app-dialog [(visible)]="showViewTemplateDialog" class="w-full max-w-4xl h-[85vh]" [fullscreenOnMobile]="true"
    (closed)="showViewTemplateDialog = false">
    <ng-template #headerTemplate>
        <div class="flex justify-between items-center w-full">
            <div class="text-sm sm:text-base font-bold truncate">{{ selectedTemplate?.name ?? 'Untitled' }}</div>
        </div>
    </ng-template>
    <ng-template #bodyTemplate>
        <div class="h-full w-full">
            @if(selectedTemplate) {
            <app-document-preview [documentId]="selectedTemplateId ?? ''" [file]="selectedTemplateAttachment" />
            }
        </div>
    </ng-template>
</app-dialog>